import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, MapPin, Youtube, Linkedin, MessageSquare } from 'lucide-react';

const Contact = () => {
  const socialLinks = [
    {
      name: 'YouTube',
      icon: Youtube,
      link: 'https://www.youtube.com/@FinanceinCanada',
      description: 'Watch tutorials and financial education videos',
      color: 'red',
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      link: 'https://www.linkedin.com/in/herambvadalkar/',
      description: 'Connect professionally',
      color: 'blue',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <Helmet>
        <title>Contact Harry Vadalkar | Canadian Investing Expert | Financial Educator Calgary | SAIT MRU Instructor</title>
        <meta name="description" content="Contact Harry Vadalkar for Canadian investing courses, CSC exam prep, IFC certification, CIRO training inquiries. Financial educator at SAIT and Mount Royal University in Calgary. CFA charterholder, Harvard & MIT certified. Book consultations for investment advice and course collaborations." />
        <meta name="keywords" content="contact Harry Vadalkar, Harry Vadalkar email, Canadian investing expert contact, financial educator Calgary, SAIT finance instructor, Mount Royal University finance, investing consultation Canada, CSC course instructor contact, IFC exam teacher, CIRO trainer contact, CFA charterholder Calgary, financial education inquiry, book Harry Vadalkar, Canadian finance expert, investing course creator, Wealthy Canadian author contact" />
        <link rel="canonical" href="https://harryvadalkar.ca/contact" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-[#020617]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full mb-6">
            <MessageSquare className="w-4 h-4" />
            <span className="font-medium">Get in Touch</span>
          </div>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
            Contact <span className="text-blue-400">Harry</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Have questions about financial education, courses, or collaboration? I'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20" data-testid="contact-form-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-white mb-6">Get in Touch</h2>
            <div className="glass rounded-2xl p-8 md:p-12">
              <Mail className="w-16 h-16 text-blue-400 mx-auto mb-6" />
              <p className="text-slate-300 text-lg mb-4">For inquiries, collaborations, or questions about courses and books:</p>
              <a 
                href="mailto:harryvadalkar@gmail.com" 
                data-testid="contact-email"
                className="text-2xl md:text-3xl font-semibold text-blue-400 hover:text-blue-300 transition-colors"
              >
                harryvadalkar@gmail.com
              </a>
            </div>
          </div>

          {/* Social Links */}
          <div className="space-y-6">
            <h3 className="font-heading text-2xl font-bold text-white text-center mb-6">Connect With Me</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Social Links */}
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${social.name.toLowerCase()}`}
                  className="glass rounded-xl p-6 flex items-start gap-4 hover:border-slate-700 transition-all group block"
                >
                  <div className={`p-3 rounded-xl ${
                    social.color === 'red' ? 'bg-red-500/20' : 'bg-blue-500/20'
                  }`}>
                    <social.icon className={`w-6 h-6 ${
                      social.color === 'red' ? 'text-red-400' : 'text-blue-400'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1 group-hover:text-blue-400 transition-colors">
                      {social.name}
                    </h3>
                    <p className="text-slate-400">{social.description}</p>
                  </div>
                </a>
              ))}
            </div>

            {/* Location */}
            <div className="glass rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-green-500/20">
                  <MapPin className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Location</h3>
                  <p className="text-slate-400">Calgary, Alberta, Canada</p>
                  <p className="text-slate-500 text-sm mt-1">Teaching at SAIT & Mount Royal University</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;